/**
 * ReservationsList — las dos presentaciones del listado.
 *
 * Escritorio: tabla densa con la convención visual de V2 (fondo alterno por
 * columna, resalte de fila al pasar el ratón, importes a la derecha, sin
 * bordes de más). Móvil: tarjetas con lo justo y un solo toque para abrir.
 *
 * Ninguna fila lleva una batería de botones: la acción es abrir la reserva.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Warning } from "@phosphor-icons/react";
import { formatMoney } from "@/lib/pmsSelectors";
import {
  BOOKING_STATUS_LABEL, STAY_STATUS_LABEL, PAYMENT_STATUS_LABEL,
} from "@/data/pmsDemoData";
import {
  TodayCard, RowList, Row, Meta, StatusPill, QuickAction, EmptyState, DenseTable,
} from "@/pages/admin/pms/v2/V2Primitives";

const STAY_TONE = { pre_arrival: "blue", checked_in: "emerald", checked_out: "stone" };
const PAY_TONE = {
  paid: "emerald",
  authorized: "blue",
  partially_paid: "amber",
  pending: "amber",
  unpaid: "red",
  failed: "red",
  partially_refunded: "stone",
  refunded: "stone",
};
const COLUMNS = [
  { key: "ref", label: "Reserva" },
  { key: "dates", label: "Fechas" },
  { key: "guest", label: "Huésped" },
  { key: "unit", label: "Tipo / unidad" },
  { key: "channel", label: "Canal" },
  { key: "stay", label: "Estancia" },
  { key: "payment", label: "Cobro" },
  { key: "total", label: "Total", align: "right" },
  { key: "attention", label: "Atención" },
];

/** "21–25 ago" — compacto y sin repetir el mes cuando es el mismo. */
export function formatRange(checkIn, checkOut) {
  const a = new Date(`${checkIn}T00:00:00Z`);
  const b = new Date(`${checkOut}T00:00:00Z`);
  const month = (d) => d.toLocaleDateString("es-ES", { month: "short", timeZone: "UTC" }).replace(".", "");
  const day = (d) => d.getUTCDate();
  return month(a) === month(b)
    ? `${day(a)}–${day(b)} ${month(b)}`
    : `${day(a)} ${month(a)} – ${day(b)} ${month(b)}`;
}

export function ReservationsTable({ views }) {
  if (views.length === 0) return <EmptyResults />;
  return (
    <DenseTable
      testId="reservations-table"
      caption="Reservas"
      columns={COLUMNS}
      rows={views.map((v) => ({ key: v.id, testId: `reservation-row-${v.id}`, view: v }))}
      renderRow={({ view }) => {
        const r = view.reservation;
        const cancelled = r.booking_status === "cancelled";
        return [
          <div className="min-w-0">
            <Link
              to={`/pms/reservations/${r.reservation_id}`}
              data-testid={`reservation-open-${r.reservation_id}`}
              className={`block truncate font-mono text-[11px] hover:underline ${
                cancelled ? "text-stone-400 line-through" : "text-stone-900 dark:text-stone-100"
              }`}
            >
              {r.reservation_id}
            </Link>
            <span className="text-[10px] text-stone-400">
              {BOOKING_STATUS_LABEL[r.booking_status]}
            </span>
          </div>,
          <div className="whitespace-nowrap">
            <div className="tabular-nums text-stone-900 dark:text-stone-100">
              {formatRange(r.check_in, r.check_out)}
            </div>
            <span className="text-[10px] text-stone-400">{r.nights} noches</span>
          </div>,
          <div className="min-w-0 max-w-[9rem]">
            <div className="truncate text-stone-900 dark:text-stone-100">{r.guest?.name || "—"}</div>
            <span className="text-[10px] text-stone-400">
              {(r.guest?.adults || 0) + (r.guest?.children || 0)} huéspedes
            </span>
          </div>,
          <div className="min-w-0 max-w-[8rem]">
            <div className="truncate text-stone-700 dark:text-stone-300">
              {view.unitType?.name || "—"}
            </div>
            <span className={`text-[10px] ${view.assignedUnit ? "text-stone-400" : "text-amber-700 dark:text-amber-300"}`}>
              {view.assignedUnit ? view.assignedUnit.villa_id : "Sin asignar"}
            </span>
          </div>,
          <span className="block max-w-[5.5rem] truncate text-stone-500 dark:text-stone-400" title={view.channel?.label}>
            {view.channel?.label}
          </span>,
          <StatusPill tone={cancelled ? "stone" : STAY_TONE[r.stay_status] || "stone"}>
            {cancelled ? BOOKING_STATUS_LABEL.cancelled : STAY_STATUS_LABEL[r.stay_status]}
          </StatusPill>,
          <StatusPill tone={PAY_TONE[r.payment_status] || "stone"}>
            {PAYMENT_STATUS_LABEL[r.payment_status]}
          </StatusPill>,
          <span className="tabular-nums whitespace-nowrap text-stone-900 dark:text-stone-100">
            {formatMoney(view.money.total_eur, view.money.currency)}
          </span>,
          <AttentionCell attention={view.attention} id={r.reservation_id} />,
        ];
      }}
    />
  );
}

function AttentionCell({ attention, id }) {
  if (attention.length === 0) {
    return <span className="text-stone-300 dark:text-stone-700" aria-label="Sin avisos">—</span>;
  }
  const first = attention[0];
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300"
      title={attention.map((a) => a.label).join(" · ")}
      data-testid={`reservation-attention-${id}`}
    >
      <Warning size={11} weight="fill" className="shrink-0" />
      <span className="truncate max-w-[8rem]">{first.label}</span>
      {attention.length > 1 && <span className="text-stone-400">+{attention.length - 1}</span>}
    </span>
  );
}

export function ReservationsCards({ views }) {
  if (views.length === 0) return <EmptyResults />;
  return (
    <TodayCard padding="p-0">
      <RowList testId="reservations-cards">
        {views.map((view) => {
          const r = view.reservation;
          const cancelled = r.booking_status === "cancelled";
          return (
            <Row key={view.id} testId={`reservation-row-${view.id}`}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className={`text-sm truncate ${
                    cancelled ? "text-stone-400 line-through" : "text-stone-900 dark:text-stone-100"
                  }`}>
                    {r.guest?.name || "—"}
                  </div>
                  <Meta items={[
                    `${formatRange(r.check_in, r.check_out)} · ${r.nights} noches`,
                    view.unitType?.name,
                    view.assignedUnit ? view.assignedUnit.villa_id : "sin asignar",
                    view.channel?.label,
                  ]} />
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <StatusPill tone={cancelled ? "stone" : STAY_TONE[r.stay_status] || "stone"}>
                      {cancelled ? BOOKING_STATUS_LABEL.cancelled : STAY_STATUS_LABEL[r.stay_status]}
                    </StatusPill>
                    <StatusPill tone={PAY_TONE[r.payment_status] || "stone"}>
                      {PAYMENT_STATUS_LABEL[r.payment_status]}
                    </StatusPill>
                    <span className="text-xs tabular-nums text-stone-700 dark:text-stone-300">
                      {formatMoney(view.money.total_eur, view.money.currency)}
                    </span>
                  </div>
                  {view.attention.length > 0 && (
                    <div
                      className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-300 inline-flex items-center gap-1"
                      data-testid={`reservation-attention-${view.id}`}
                    >
                      <Warning size={11} weight="fill" />
                      {view.attention.map((a) => a.label).join(" · ")}
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  <QuickAction
                    label="Abrir"
                    to={`/pms/reservations/${view.id}`}
                    testId={`reservation-open-${view.id}`}
                  />
                </div>
              </div>
            </Row>
          );
        })}
      </RowList>
    </TodayCard>
  );
}

function EmptyResults() {
  return (
    <EmptyState testId="reservations-empty">
      Ninguna reserva coincide con la búsqueda o los filtros.
    </EmptyState>
  );
}
